import { useState, useRef, useEffect, memo } from "react";
import PropTypes from "prop-types";
import { FaChevronDown, FaLayerGroup, FaInfoCircle } from "react-icons/fa";
import { layerGroups } from "../config/layers";
import "./LayerControl.css";

const RBFS_MODEL_MODES = [
  {
    id: "baseline",
    label: "Baseline",
    modelCategory: "Baseline Model",
  },
  {
    id: "alternative",
    label: "Alternative Analysis",
    modelCategory: "Alternative Analysis Model",
  },
];

function isRbfsLayer(layer) {
  return layerGroups.rbfs.layers.some((item) => item.id === layer.id);
}

function withModelMode(layer, modeId) {
  if (!isRbfsLayer(layer)) return layer;

  const mode =
    RBFS_MODEL_MODES.find((item) => item.id === modeId) || RBFS_MODEL_MODES[0];
  return {
    ...layer,
    modelCategory: mode.modelCategory,
  };
}

function LayerOption({ layer, idPrefix, checked, disabled, onToggle }) {
  return (
    <div className="layer-option">
      <input
        type="checkbox"
        id={`${idPrefix}-${layer.id}`}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onToggle(layer, e.target.checked)}
      />
      <label htmlFor={`${idPrefix}-${layer.id}`} className="layer-option-text">
        {layer.title}
      </label>
    </div>
  );
}

LayerOption.propTypes = {
  layer: PropTypes.object.isRequired,
  idPrefix: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

const LayerControl = ({
  onToggleLayer,
  isAuthenticated,
  setShowAuthDialog,
  isLayerLoading,
}) => {
  const [isLayerMenuOpen, setIsLayerMenuOpen] = useState(false);
  const [checkedLayers, setCheckedLayers] = useState(new Set(["counties"]));
  const [regionModes, setRegionModes] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    sources: false,
    rbfs: false,
    ms2: false,
    counties: true,
  });
  const layerMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        layerMenuRef.current &&
        !layerMenuRef.current.contains(event.target)
      ) {
        setIsLayerMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const getNewCheckedLayers = (prev, layer, isChecked) => {
    const newCheckedLayers = new Set(prev);

    if (layer.id === "counties") {
      newCheckedLayers.clear();
      if (isChecked) {
        newCheckedLayers.add("counties");
      }
    } else {
      if (newCheckedLayers.has("counties")) {
        newCheckedLayers.delete("counties");
      }

      if (isChecked) {
        newCheckedLayers.add(layer.id);
      } else {
        newCheckedLayers.delete(layer.id);
      }
    }
    return newCheckedLayers;
  };

  const clearRegionMode = (layerId) => {
    setRegionModes((prev) => {
      if (!prev[layerId]) return prev;
      const next = { ...prev };
      delete next[layerId];
      return next;
    });
  };

  const turnOffSourceLayer = (layerId) => {
    const layerObj = findLayerById(layerId);
    if (!layerObj) return;

    const modes = regionModes[layerId];
    const activeModes = RBFS_MODEL_MODES.filter((mode) => modes?.[mode.id]);
    if (activeModes.length > 0) {
      activeModes.forEach((mode) => {
        onToggleLayer(withModelMode(layerObj, mode.id), false);
      });
    } else {
      onToggleLayer(layerObj, false);
    }
    clearRegionMode(layerId);
  };

  const handleLayerToggleSideEffects = (
    layer,
    isChecked,
    currentCheckedLayers,
  ) => {
    if (layer.id === "counties") {
      if (isChecked) {
        currentCheckedLayers.forEach((id) => {
          if (id !== "counties") {
            turnOffSourceLayer(id);
          }
        });
        onToggleLayer(layer, true);
      } else {
        onToggleLayer(layer, false);
      }
      return;
    }

    // We are toggling a source
    if (isChecked) {
      if (currentCheckedLayers.has("counties")) {
        const countiesLayer = findLayerById("counties");
        if (countiesLayer) {
          onToggleLayer(countiesLayer, false);
        }
      }
      if (isRbfsLayer(layer)) {
        setRegionModes((prev) => ({
          ...prev,
          [layer.id]: { baseline: true, alternative: false },
        }));
        onToggleLayer(withModelMode(layer, "baseline"), true);
      } else {
        onToggleLayer(layer, true);
      }
    } else {
      turnOffSourceLayer(layer.id);
    }
  };

  const handleModelModeChange = (layer, modeId, isChecked) => {
    if (!isAuthenticated) {
      setShowAuthDialog(true);
      return;
    }
    if (isLayerLoading) return;

    const current = regionModes[layer.id] || {
      baseline: true,
      alternative: false,
    };
    if (Boolean(current[modeId]) === isChecked) return;

    setRegionModes((prev) => ({
      ...prev,
      [layer.id]: {
        ...current,
        ...prev[layer.id],
        [modeId]: isChecked,
      },
    }));
    onToggleLayer(withModelMode(layer, modeId), isChecked);
  };

  const handleLayerToggle = (layer, isChecked) => {
    if (!isAuthenticated) {
      setShowAuthDialog(true);
      return;
    }

    setCheckedLayers((prev) => {
      const newCheckedLayers = getNewCheckedLayers(prev, layer, isChecked);
      // We need to pass the *previous* state to side effects handler because
      // inside setCheckedLayers we are calculating the new state but side effects
      // might depend on what was there before or just the action itself.
      // Actually, the side effects logic in the original code used `checkedLayers` which is the *current* state (before update)
      // EXCEPT for the part where it iterates over `checkedLayers` to turn off other sources when counties is selected.
      // Wait, `checkedLayers` in the component scope is the state from the *previous* render.
      // The original code used `checkedLayers` (state) inside `handleLayerToggle`.

      // Let's look at the original side effects logic again.
      // It used `checkedLayers` (the state variable).

      return newCheckedLayers;
    });

    // Call side effects
    // Note: We are using `checkedLayers` here which is the state *before* the update we just scheduled.
    // This matches the original behavior where it used `checkedLayers` from the closure.
    handleLayerToggleSideEffects(layer, isChecked, checkedLayers);
  };

  // Helper to find layer object by ID
  const findLayerById = (id) => {
    if (id === "counties") return layerGroups.counties.layers[0];

    // Search in sources
    for (const sectionId of layerGroups.sources.sections) {
      const section = layerGroups[sectionId];
      const found = section.layers.find((l) => l.id === id);
      if (found) return found;
    }
    return null;
  };

  const renderLayerGroup = (groupId) => {
    const group = layerGroups[groupId];
    if (!group) return null;

    return (
      <div className="layer-parent-section">
        {/* fixed this accessibility issue,make the clickable div element keyboard-accessible by:
                Adding a tabIndex prop to make it focusable
                Adding an onKeyDown handler for keyboard interaction
                Using the semantically appropriate ARIA role
                Adding appropriate ARIA labels  */}
        <div
          className="layer-parent-header"
          onClick={() => toggleSection(groupId)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleSection(groupId);
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={expandedSections[groupId]}
          aria-controls={`section-${groupId}`}
        >
          <div className="layer-parent-title-wrapper">
            <span className="layer-parent-title">
              {group.title}
              {group.tooltip && (
                <div className="layer-help-tooltip" title={group.tooltip}>
                  <FaInfoCircle />
                </div>
              )}
            </span>
          </div>
          <FaChevronDown
            className={`accordion-icon ${expandedSections[groupId] ? "expanded" : ""}`}
          />
        </div>
        <div
          id={`section-${groupId}`}
          className={`layer-parent-content ${expandedSections[groupId] ? "expanded" : ""}`}
        >
          {group.sections ? (
            group.sections.map((sectionId) => (
              <div key={sectionId} className="layer-subsection">
                <div
                  className="layer-subsection-header"
                  onClick={() => toggleSection(sectionId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleSection(sectionId);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={expandedSections[sectionId]}
                  aria-controls={`section-${sectionId}`}
                >
                  <div className="layer-parent-title-wrapper">
                    <span className="layer-subsection-title">
                      {layerGroups[sectionId].title}
                    </span>
                  </div>
                  <FaChevronDown
                    className={`accordion-icon ${expandedSections[sectionId] ? "expanded" : ""}`}
                  />
                </div>
                <div
                  id={`section-${sectionId}`}
                  className={`layer-subsection-content ${expandedSections[sectionId] ? "expanded" : ""}`}
                >
                  <div className="layer-section-content">
                    {layerGroups[sectionId].layers.map((layer) => (
                      <div key={layer.id}>
                        <LayerOption
                          layer={layer}
                          idPrefix={sectionId}
                          checked={checkedLayers.has(layer.id)}
                          disabled={isLayerLoading}
                          onToggle={handleLayerToggle}
                        />
                        {sectionId === "rbfs" &&
                          checkedLayers.has(layer.id) && (
                            <div
                              className="layer-model-modes"
                              role="group"
                              aria-label={`${layer.title} model category`}
                            >
                              {RBFS_MODEL_MODES.map((mode) => (
                                <div className="layer-model-mode" key={mode.id}>
                                  <input
                                    type="checkbox"
                                    id={`${sectionId}-${layer.id}-${mode.id}`}
                                    checked={Boolean(
                                      regionModes[layer.id]?.[mode.id],
                                    )}
                                    disabled={isLayerLoading}
                                    onChange={(e) =>
                                      handleModelModeChange(
                                        layer,
                                        mode.id,
                                        e.target.checked,
                                      )
                                    }
                                  />
                                  <label
                                    htmlFor={`${sectionId}-${layer.id}-${mode.id}`}
                                    className="layer-option-text"
                                  >
                                    <span
                                      className={`layer-mode-swatch layer-mode-swatch-${mode.id}`}
                                      aria-hidden="true"
                                    />
                                    {mode.label}
                                  </label>
                                </div>
                              ))}
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="layer-section-content">
              {group.layers.map((layer) => (
                <LayerOption
                  key={layer.id}
                  layer={layer}
                  idPrefix={groupId}
                  checked={checkedLayers.has(layer.id)}
                  disabled={isLayerLoading}
                  onToggle={handleLayerToggle}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLayerMenuOpen(!isLayerMenuOpen);
  };

  return (
    <div className="layer-control" ref={layerMenuRef}>
      <button
        className="layer-control-button"
        onClick={handleButtonClick}
        aria-label="Toggle layer menu"
        type="button"
      >
        <FaLayerGroup size={20} />
      </button>
      {isLayerMenuOpen && (
        <div className="layer-menu">
          {renderLayerGroup("sources")}
          {renderLayerGroup("counties")}
        </div>
      )}
    </div>
  );
};

LayerControl.displayName = "LayerControl";

LayerControl.propTypes = {
  onToggleLayer: PropTypes.func,
  isAuthenticated: PropTypes.bool,
  setShowAuthDialog: PropTypes.func,
  isLayerLoading: PropTypes.bool,
};

export default memo(LayerControl);
